import {
  type ChangeEvent,
  type HTMLInputTypeAttribute,
  type JSX,
  type ReactNode,
  useId,
} from 'react'
import {
  Controller,
  type RegisterOptions,
  type UseFormReturn,
} from 'react-hook-form'

import { FormControl, FormHelperText, FormLabel, Input } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormV3Values } from '../../../hooks/marketplacePartnerRegisterPageFormV3RHFModel'

type MarketplacePartnerRegisterPageFormV3InputFieldProps<
  FieldName extends keyof MarketplacePartnerRegisterPageFormV3Values,
> = {
  fieldName: FieldName
  form: UseFormReturn<MarketplacePartnerRegisterPageFormV3Values>
  label: ReactNode
  required?: boolean
  rules?: RegisterOptions<MarketplacePartnerRegisterPageFormV3Values, FieldName>
  type?: HTMLInputTypeAttribute
}

export const MarketplacePartnerRegisterPageFormV3InputField = <
  FieldName extends keyof MarketplacePartnerRegisterPageFormV3Values,
>({
  fieldName,
  form,
  label,
  required = false,
  rules,
  type = 'text',
}: MarketplacePartnerRegisterPageFormV3InputFieldProps<FieldName>): JSX.Element => {
  const fieldId = useId()

  return (
    <Controller
      control={form.control}
      name={fieldName}
      render={({ field, fieldState }) => {
        const handleBlur = field.onBlur
        const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
          field.onChange(event.target.value)
        }

        return (
          <FormControl
            data-qa-id={`sellerForm-formControl-${fieldName}`}
            error={Boolean(fieldState.error)}
            required={required}
            value={field.value}
          >
            <FormLabel htmlFor={fieldId}>{label}</FormLabel>
            {type === 'textarea' ? (
              <Input
                data-qa-id={`marketplace-partner-register-page-form-input-field-textarea-${fieldName}`}
                id={fieldId}
                multiline
                onBlur={handleBlur}
                onChange={handleChange}
                slotProps={{
                  input: {
                    'data-qa-id': `message-form-§emailForm.${fieldName}`,
                  },
                }}
              />
            ) : (
              <Input
                data-qa-id={`marketplace-partner-register-page-form-input-field-input-${fieldName}`}
                id={fieldId}
                onBlur={handleBlur}
                onChange={handleChange}
                slotProps={{
                  input: {
                    'data-qa-id': `message-form-§emailForm.${fieldName}`,
                  },
                }}
                type={type}
              />
            )}
            {fieldState.error?.message ? (
              <FormHelperText
                data-qa-id={`global-MessageForm-${fieldName}-error-msg`}
                error
              >
                {fieldState.error.message}
              </FormHelperText>
            ) : null}
          </FormControl>
        )
      }}
      rules={rules}
    />
  )
}
